/**
 * Unified utility for fixing asset paths and dynamic imports in environments
 * where the base URL is not at the root.
 *
 * This combines functionality from viteImportFix.ts and dynamicImportFix.ts
 * to reduce code duplication and provide a single source of truth for
 * asset path handling.
 *
 * Note: This utility is only necessary for applications deployed to non-root paths
 * (e.g., '/test/' or '/dev/'). If your application is deployed exclusively at the
 * root path ('/'), you likely don't need this utility. See the Lambda asset loading
 * guide documentation for more information on the "Root Path Only Solution".
 */

// Helper function to check if a URL needs the base path prefix
function needsBasePath(url: string, basePath: string): boolean {
  return (
    typeof url === 'string' &&
    (url.startsWith('/assets/') || url.startsWith('/')) &&
    !url.startsWith(basePath)
  );
}

// Helper function to add base path to a URL if needed
function addBasePath(url: string, basePath: string): string {
  if (needsBasePath(url, basePath)) {
    return `${basePath}${url}`;
  }
  return url;
}

/**
 * Main function to apply all asset path fixes
 * Combines functionality from both viteImportFix and dynamicImportFix
 */
export function applyAssetPathFixes() {
  // Check if we're in a browser environment
  if (typeof window === 'undefined') return;

  // Get the base URL from the window object
  const basePath = (window as any).__BASE_PATH__ || '';

  // Only apply the fixes if we have a non-root base path
  if (!basePath || basePath === '/') return;

  // Make the base path available globally for other scripts
  (window as any).__vite_base_path = basePath;

  // 1. Fix existing DOM elements (modulepreload, preload links)
  fixExistingDomElements(basePath);

  // 2. Patch Vite's dynamic import mechanism
  patchViteImports(basePath);

  // 3. Patch browser APIs for future asset loading
  patchBrowserApis(basePath);

  // 4. Add error recovery for failed asset loads
  setupErrorRecovery(basePath);
}

/**
 * Fix existing DOM elements with asset paths
 * Combines functionality from both utilities
 */
function fixExistingDomElements(basePath: string) {
  // Fix modulepreload links (from viteImportFix)
  document.querySelectorAll('link[rel="modulepreload"]')
    .forEach(link => {
      const href = link.getAttribute('href');
      if (href && needsBasePath(href, basePath)) {
        link.setAttribute('href', addBasePath(href, basePath));
      }
    });

  // Fix preload links for CSS (from dynamicImportFix)
  document.querySelectorAll('link[rel="preload"][as="style"], link[rel="stylesheet"]')
    .forEach(link => {
      const href = link.getAttribute('href');
      if (href && needsBasePath(href, basePath)) {
        const newHref = addBasePath(href, basePath);
        link.setAttribute('href', newHref);
      }
    });

  // Fix script src attributes
  document.querySelectorAll('script[src]').forEach(script => {
    const src = script.getAttribute('src');
    if (src && needsBasePath(src, basePath)) {
      script.setAttribute('src', addBasePath(src, basePath));
    }
  });
}

/**
 * Patch Vite's dynamic import mechanism
 * From viteImportFix
 */
function patchViteImports(basePath: string) {
  // Patch __vitePreload function
  const originalVitePreload = (window as any).__vitePreload;
  if (typeof originalVitePreload === 'function') {
    (window as any).__vitePreload = function(factory: any, deps?: string[]) {
      // Fix paths in dependencies array
      if (deps && Array.isArray(deps)) {
        deps = deps.map(dep => addBasePath(dep, basePath));
      }
      return originalVitePreload(factory, deps);
    };
  } else {
    // Set up a getter/setter to intercept when __vitePreload is defined
    let _vitePreload: any = undefined;
    Object.defineProperty(window, '__vitePreload', {
      get() {
        return _vitePreload;
      },
      set(value) {
        _vitePreload = function(factory: any, deps?: string[]) {
          if (deps && Array.isArray(deps)) {
            deps = deps.map(dep => addBasePath(dep, basePath));
          }
          return value(factory, deps);
        };
      },
      configurable: true
    });
  }

  // Patch import function (combined from both utilities)
  const originalImport = (window as any).__import__ || (window as any).import || Function.prototype;
  (window as any).__import__ = function(url: string) {
    // Handle relative imports normally
    if (url.startsWith('./') || url.startsWith('../')) {
      return originalImport.call(this, url);
    }

    // Add base path to absolute imports
    if (url.startsWith('/') && !url.startsWith(basePath)) {
      return originalImport.call(this, addBasePath(url, basePath));
    }

    return originalImport.call(this, url);
  };

  // Expose as window.import for compatibility
  if (!(window as any).import) {
    (window as any).import = (window as any).__import__;
  }
}

/**
 * Patch browser APIs for future asset loading
 * From dynamicImportFix
 */
function patchBrowserApis(basePath: string) {
  // Patch fetch API
  const originalFetch = window.fetch;
  window.fetch = function(input: RequestInfo | URL, init?: RequestInit): Promise<Response> {
    if (typeof input === 'string' && needsBasePath(input, basePath)) {
      return originalFetch.call(this, addBasePath(input, basePath), init);
    }
    return originalFetch.call(this, input, init);
  };

  // Patch XMLHttpRequest
  const originalOpen = XMLHttpRequest.prototype.open;
  XMLHttpRequest.prototype.open = function(
    method: string,
    url: string | URL,
    async: boolean = true,
    username?: string | null,
    password?: string | null
  ): void {
    if (typeof url === 'string' && needsBasePath(url, basePath)) {
      return originalOpen.call(this, method, addBasePath(url, basePath), async, username, password);
    }
    return originalOpen.call(this, method, url, async, username, password);
  };

  // Patch document.createElement for <link> and <script> elements
  const originalCreateElement = document.createElement;
  document.createElement = function(tagName: string, options?: ElementCreationOptions): HTMLElement {
    const element = originalCreateElement.call(document, tagName, options);

    if (tagName.toLowerCase() === 'link' || tagName.toLowerCase() === 'script') {
      const originalSetAttribute = element.setAttribute;
      element.setAttribute = function(name: string, value: string): void {
        if ((name === 'href' || name === 'src') && needsBasePath(value, basePath)) {
          return originalSetAttribute.call(this, name, addBasePath(value, basePath));
        }
        return originalSetAttribute.call(this, name, value);
      };
    }

    return element;
  };
}

/**
 * Set up error recovery for failed asset loads
 * From dynamicImportFix
 */
function setupErrorRecovery(basePath: string) {
  window.addEventListener('error', function(event) {
    const target = event.target as HTMLElement;
    if (target && (target.tagName === 'LINK' || target.tagName === 'SCRIPT')) {
      const src = (target as HTMLScriptElement).src || (target as HTMLLinkElement).href;
      if (src && src.includes('/assets/') && !src.includes(basePath)) {
        // Extract the asset path after /assets/
        const assetPath = src.split('/assets/')[1];
        if (!assetPath) return;

        // Create a new element with the correct path
        if (target.tagName === 'LINK') {
          const link = target as HTMLLinkElement;
          const newLink = document.createElement('link');
          newLink.rel = link.rel;
          newLink.href = `${basePath}/assets/${assetPath}`;
          newLink.type = link.type;
          document.head.appendChild(newLink);
        } else if (target.tagName === 'SCRIPT') {
          const script = target as HTMLScriptElement;
          const newScript = document.createElement('script');
          newScript.src = `${basePath}/assets/${assetPath}`;
          newScript.type = script.type;
          document.head.appendChild(newScript);
        }
      }
    }
  }, true);
}

/**
 * Specifically fix CSS links
 * Extracted from dynamicImportFix.fixPreloadedCssLinks
 */
export function fixCssLinks() {
  if (typeof window === 'undefined') return;

  const basePath = (window as any).__BASE_PATH__ || '';
  if (!basePath || basePath === '/') return;

  // Fix all CSS-related links
  document.querySelectorAll('link[rel="preload"][as="style"]')
    .forEach(link => {
      const href = link.getAttribute('href');
      if (href && needsBasePath(href, basePath)) {
        const newHref = addBasePath(href, basePath);
        link.setAttribute('href', newHref);

        // Also create a stylesheet link with the correct path
        const styleLink = document.createElement('link');
        styleLink.rel = 'stylesheet';
        styleLink.href = newHref;
        document.head.appendChild(styleLink);
      }
    });
}
